import { PDFDocument, StandardFonts, rgb, PDFPage, PDFFont } from 'pdf-lib';
import { COORDS } from './pdfCoordinates.ts';
import { getPolizzaCompleta } from './getPolizza.ts';

const FONT_SIZE = 10;
const COLOR     = rgb(0, 0, 0);

/** Ritorna la data odierna formattata gg/mm/aaaa (fuso Roma) */
function oggiFormattato(): string {
  return new Intl.DateTimeFormat('it-IT', {
    timeZone: 'Europe/Rome',
    day:   '2-digit',
    month: '2-digit',
    year:  'numeric',
  }).format(new Date());
}

/** Disegna testo con opzione di allineamento */
function drawAlignedText(
  page: PDFPage,
  text: string,
  coord: { x: number; y: number },
  font: PDFFont,
  size: number,
  align: 'left' | 'right' = 'left'
) {
  if (!text) return;

  let x = coord.x;
  if (align === 'right') {
    const textWidth = font.widthOfTextAtSize(text, size);
    x = coord.x - textWidth; // calcola per allineare a destra
  }

  page.drawText(text, {
    x,
    y: coord.y,
    size,
    font,
    color: COLOR,
  });
}

export async function generaPdfPolizza(idPolizza: string): Promise<Blob> {
  /* 1. Dati dal DB */
  const p         = await getPolizzaCompleta(idPolizza);
  const tipologia = p.pacchetto?.tipologia ?? 'A';

  /* 2. Template PDF */
  const buf = await fetch(`/pdf/modellou.pdf`).then(r => r.arrayBuffer());
  const pdf = await PDFDocument.load(buf);

  const page      = pdf.getPage(0);
  const fontReg   = await pdf.embedFont(StandardFonts.Helvetica);
  const fontBold  = await pdf.embedFont(StandardFonts.HelveticaBold);

  /* 3. AGENZIA, DATA (regular, right aligned) */
  drawAlignedText(page, `${p.agente?.agenzia?.nome ?? ''}, ${oggiFormattato()}`, COORDS.agenziaData, fontReg, FONT_SIZE, 'right');

  /* 4. NOMINATIVO / RAG. SOCIALE (bold, right aligned) */
  const nominativo = p.cliente_privato
    ? `${p.nome_cliente ?? ''} ${p.cognome_cliente ?? ''}`.trim()
    : p.ragione_sociale ?? '';
  drawAlignedText(page, nominativo, COORDS.nominativo, fontBold, FONT_SIZE, 'right');

  /* 5. CODICE FISCALE / P. IVA (bold, right aligned) */
  const cfPiva = p.cliente_privato ? p.codice_fiscale : p.partita_iva;
  drawAlignedText(page, (cfPiva ?? '').trim(), COORDS.cf_piva, fontBold, FONT_SIZE, 'right');

  /* 6. TARGA (bold, left aligned) */
  drawAlignedText(page, p.targa ?? '', COORDS.targa, fontBold, FONT_SIZE, 'left');

  /* 7. PACCHETTO ACQUISTATO (bold, left aligned) */
  drawAlignedText(page, (p.pacchetto?.nome ?? '').trim(), COORDS.pacchetto, fontBold, FONT_SIZE, 'left');

  /* 8. Salva PDF */
  const bytes = await pdf.save();
  const arrayBuffer = new Uint8Array(bytes).buffer;
  return new Blob([arrayBuffer], { type: 'application/pdf' });
}
